/**
 * The decoration pricing engine. Reference implementation.
 *
 * Ported verbatim from the approved prototype (pricing.js) and specified in
 * specs/shared/pricing-engine.md. The Edge Functions import this module; the
 * Dart port in the Flutter app must produce identical output. Both are held to
 * the vectors in ./vectors.json (Constitution §7).
 *
 * Constitution §1: the client may run this locally for instant UI feedback, but
 * the number money moves against is the one the server computes at the moment
 * of the transaction.
 */

import { round2 } from '../money';

export type MethodKey = 'embroidery' | 'screen' | 'dtf' | 'sublimation';
export type LocationKey = 'front' | 'back' | 'left_chest' | 'sleeve';

/** A row of `print_methods`, in dollars. The table is the runtime source. */
export interface PrintMethod {
  key: string;
  name: string;
  blurb: string | null;
  moq: number;
  /** null = unlimited / full colour */
  max_colours: number | null;
  turnaround: string;
  setup_per_colour_per_location: number;
  setup_per_location: number;
  print_per_location: number;
  extra_colour_cost: number;
  specialty: string | null;
}

export interface EstimateInput {
  qty?: number | string;
  colours?: number | string;
  locations?: string[];
}

export interface Estimate {
  method_key: string;
  method_name: string;
  turnaround: string;
  qty: number;
  /** null for full-colour methods (dtf, sublimation) */
  colours: number | null;
  locations: LocationKey[];
  garment_price: number;
  print_per_unit: number;
  unit_price: number;
  setup_total: number;
  total: number;
  moq: number;
  moq_ok: boolean;
  tier_discount: number;
  warnings: string[];
}

export const LOCATIONS: { key: LocationKey; name: string }[] = [
  { key: 'front', name: 'Front' },
  { key: 'back', name: 'Back' },
  { key: 'left_chest', name: 'Left chest' },
  { key: 'sleeve', name: 'Sleeve' },
];

export function locationLabel(key: string): string {
  return LOCATIONS.find((l) => l.key === key)?.name ?? key;
}

/**
 * Volume tier multiplier. Applied to the per-unit print cost only, never to the
 * garment price. Spec §3.
 */
export function tierMultiplier(qty: number): number {
  if (qty >= 250) return 0.5;
  if (qty >= 100) return 0.6;
  if (qty >= 50) return 0.7;
  if (qty >= 12) return 0.85;
  return 1.0;
}

function toInt(v: number | string | undefined, fallback: number): number {
  const n = typeof v === 'number' ? Math.trunc(v) : parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * The formula, spec §4. `basePrice` is the blank's GST-inclusive dollar price.
 *
 * **So are `method`'s rates.** They are added to `basePrice` directly and this
 * function never adds or removes tax, so they are on whatever basis the base
 * price is on, and constitution §2 fixes that: "Display prices are
 * GST-inclusive". The output side is proved by the vectors, where
 * `exGst(109228)` is 99298, the commission base G1 asserts. The input side was
 * stated for `basePrice` and not for the rates, which are the numbers a client
 * actually types in. R-299.
 */
export function computeEstimate(
  basePrice: number,
  method: PrintMethod,
  opts: EstimateInput = {},
): Estimate {
  const warnings: string[] = [];
  const qty = Math.max(1, toInt(opts.qty, 1) || 1);

  // Unknown location keys are silently dropped; an empty list falls back to front.
  let locations = (Array.isArray(opts.locations) && opts.locations.length
    ? opts.locations
    : ['front']
  ).filter((l): l is LocationKey => LOCATIONS.some((x) => x.key === l));
  if (!locations.length) locations = ['front'];
  const L = locations.length;

  // Colours only drive price for limited-colour methods.
  let colours = toInt(opts.colours, 1) || 1;
  if (method.max_colours == null) {
    colours = 1;
  } else if (colours > method.max_colours) {
    warnings.push(
      `${method.name} supports up to ${method.max_colours} colours - capped at ${method.max_colours}.`,
    );
    colours = method.max_colours;
  } else if (colours < 1) {
    colours = 1;
  }

  const mult = tierMultiplier(qty);
  const perLocationPrint =
    method.print_per_location + (colours - 1) * method.extra_colour_cost;
  const printPerUnit = round2(L * perLocationPrint * mult);
  const unitPrice = round2(basePrice + printPerUnit);

  const setupTotal = round2(
    L * (method.setup_per_colour_per_location * colours + method.setup_per_location),
  );
  const total = round2(unitPrice * qty + setupTotal);

  if (qty < method.moq) {
    warnings.push(`${method.name} has a minimum order of ${method.moq} units.`);
  }

  return {
    method_key: method.key,
    method_name: method.name,
    turnaround: method.turnaround,
    qty,
    colours: method.max_colours == null ? null : colours,
    locations,
    garment_price: round2(basePrice),
    print_per_unit: printPerUnit,
    unit_price: unitPrice,
    setup_total: setupTotal,
    total,
    moq: method.moq,
    moq_ok: qty >= method.moq,
    tier_discount: Math.round((1 - mult) * 100),
    warnings,
  };
}
